import { useState } from "react";
import ErrorBox from "../../../components/Error/Box";
import { Team } from "types";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Spinner from "@/components/Spinner";
import { Avatar } from "@radix-ui/react-avatar";
import { AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import Tippy from "@tippyjs/react";
import Icon from "@/icons";
import ErrorBoundary from "@/ErrorBoundary";

export default function AppHomeDiscoverTeams() {
    const [search, setSearch] = useState("");

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["teams", "discovery", search],
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/discovery/teams?q=${encodeURIComponent(search)}`);
            if (!response.ok) throw new Error("Failed to fetch teams data");
            return response.json();
        },
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    type TeamDiscover = Team & { memberLength: Number, discoverableInvite: string };

    return (
        <>
            <div className="max-h-screen overflow-auto p-4 sm:px-32 select-all" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(27 27 29)" }}>
                <h1 className="text-gray-300 font-semibold text-2xl mb-6">Discover Teams</h1>
                <div className="flex w-full items-center mb-6">
                    <div className="flex-1">
                        <input
                            type="text"
                            className="w-full h-12 px-4 py-2 bg-brand-dark text-white rounded-lg focus:outline-none"
                            placeholder="Search for a team"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                {isLoading && (
                    <div className="flex flex-col w-full h-64 items-center justify-center">
                        <Spinner />
                    </div>
                )}
                {!isLoading && data?.length === 0 && (
                    <div className="flex flex-col w-full h-64 items-center justify-center">
                        <h1 className="text-gray-300 font-semibold text-2xl">No teams found</h1>
                    </div>
                )}
                {isError && (
                    <ErrorBox message={(error as Error).message} />
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-4">
                    {data?.map((team: TeamDiscover) => (
                        <ErrorBoundary>
                            <div key={team.id} className="shadow-xl flex flex-col bg-brand-dark rounded-lg overflow-hidden">
                                <div className="relative w-full h-36 border-b-2 border-brand-green">
                                    {team.banner ? (
                                        <img src={team.banner} draggable="false" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full object-cover bg-brand-back" />

                                    )}
                                    <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-brand-dark" />

                                    <div className="absolute w-full -bottom-8 left-2 flex items-center space-x-2">
                                        <div className="items-end flex space-x-2">
                                            <Avatar className="rounded-lg border-2 bg-brand-darkest border-brand-default">
                                                <AvatarImage src={team.profilePicture} className="w-16 rounded-lg" />
                                                <AvatarFallback className="px-7 py-5">
                                                    {team.name[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="items-center flex space-x-1 ">
                                                <h2 className="text-white font-semibold text-lg items-end">{team.name}</h2>
                                                {team.isVerified && (
                                                    <Tippy content="Verified Team" placement="top">
                                                        <div className="flex items-center justify-center w-6 h-6 rounded-full text-brand-green">
                                                            <Icon name="verified" size={"24"} />
                                                        </div>
                                                    </Tippy>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 mt-4 flex flex-col min-h-48 max-h-48 w-full break-all">
                                    <p className="text-gray-400 text-sm mt-2">{(team?.description || "").slice(0, 140)} {(team.description || "").length > 140 ? "..." : ""}</p>
                                    <div className="mt-4 flex justify-between items-end h-full">
                                        <Link to={`/invite/${team.discoverableInvite}`}>
                                            <Button className="bg-brand-green px-6 py-3 rounded-lg text-white font-semibold hover:bg-green-600">
                                                Apply
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </ErrorBoundary>
                    ))}

                </div>
            </div>
        </>
    );
}
